import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { inr } from "@/lib/format";
import { Users, UserPlus, CalendarClock, CheckCircle2, FileText, IndianRupee, PhoneCall, TrendingUp, Inbox, FolderOpen } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { QuickActions } from "@/components/QuickActions";

const STATUS_COLORS = {
  New: "#3B82F6", "Not Connected": "#94A3B8", "Not Interested": "#F43F5E", "Follow-up": "#F59E0B",
  Interested: "#10B981", Login: "#6366F1", "Invalid No": "#A1A1AA", Contacted: "#0EA5E9", Rejected: "#E11D48",
};

function Kpi({ icon: Icon, label, value, color, bg, testid, loading }) {
  return (
    <Card data-testid={testid} className="p-5 border-slate-200/80 dark:border-slate-800/80">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
          {loading ? <Skeleton className="h-8 w-20 mt-2" /> : <div className="text-3xl font-extrabold font-num text-slate-900 dark:text-slate-50 mt-1">{value}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`h-5 w-5 ${color}`} /></div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCoordTeam = ["Admin", "Manager", "Team Leader", "Coordinator"].includes(user?.role);
  const { data: stats, isLoading } = useQuery({ queryKey: ["dashboard-stats"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: byStatus = [], isLoading: l2 } = useQuery({ queryKey: ["leads-by-status"], queryFn: async () => (await api.get("/analytics/leads-by-status")).data });
  const { data: qCount } = useQuery({ queryKey: ["queries-count-nav"], queryFn: async () => (await api.get("/queries/count")).data, refetchInterval: 60000 });
  const { data: poolApps } = useQuery({ queryKey: ["coord-apps", "pool"], queryFn: async () => (await api.get("/coordinator/applications", { params: { scope: "pool" } })).data, enabled: isCoordTeam, refetchInterval: 60000 });
  const openQueries = qCount?.inbox_open ?? qCount?.total_open ?? 0;
  const poolCount = Array.isArray(poolApps) ? poolApps.length : 0;

  const num = (v) => (v ?? 0).toLocaleString("en-IN");
  const kpis = [
    { icon: Users, label: "Total Leads", value: num(stats?.total_leads), color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950/40", testid: "kpi-total-leads" },
    { icon: UserPlus, label: "New Leads", value: num(stats?.new_leads), color: "text-sky-600", bg: "bg-sky-50 dark:bg-sky-950/40", testid: "kpi-new-leads" },
    { icon: CalendarClock, label: "Follow-ups", value: num(stats?.followups), color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950/40", testid: "kpi-followups" },
    { icon: CheckCircle2, label: "Converted", value: num(stats?.converted), color: "text-emerald-600", bg: "bg-emerald-50 dark:bg-emerald-950/40", testid: "kpi-converted" },
    { icon: FileText, label: "Applications", value: num(stats?.total_applications), color: "text-indigo-600", bg: "bg-indigo-50 dark:bg-indigo-950/40", testid: "kpi-applications" },
    { icon: CheckCircle2, label: "Disbursed", value: num(stats?.disbursed), color: "text-teal-600", bg: "bg-teal-50 dark:bg-teal-950/40", testid: "kpi-disbursed" },
    { icon: IndianRupee, label: "Disbursed Amt", value: inr(stats?.disbursed_amount), color: "text-green-600", bg: "bg-green-50 dark:bg-green-950/40", testid: "kpi-disbursed-amount" },
    { icon: PhoneCall, label: "Calls Today", value: num(stats?.calls_today), color: "text-violet-600", bg: "bg-violet-50 dark:bg-violet-950/40", testid: "kpi-calls-today" },
  ];

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} subtitle="Live overview of your loan pipeline, synced with the mobile app." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {kpis.map((k) => <Kpi key={k.testid} {...k} loading={isLoading} />)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mt-4 sm:mt-5">
        <Card data-testid="workload-open-queries" onClick={() => navigate("/queries")} className="p-5 cursor-pointer border-slate-200/80 dark:border-slate-800/80 hover:border-rose-300 dark:hover:border-rose-800 hover:shadow-md transition-all flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-rose-50 dark:bg-rose-950/40 flex items-center justify-center"><Inbox className="h-5 w-5 text-rose-600" /></div>
            <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Open Queries</div><div className="text-sm text-slate-500">Awaiting your reply</div></div>
          </div>
          <div className="text-3xl font-extrabold font-num text-slate-900 dark:text-slate-50">{num(openQueries)}</div>
        </Card>
        {isCoordTeam && (
          <Card data-testid="workload-pool-files" onClick={() => navigate("/coord-files")} className="p-5 cursor-pointer border-slate-200/80 dark:border-slate-800/80 hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md transition-all flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center"><FolderOpen className="h-5 w-5 text-blue-600" /></div>
              <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Files in Pool</div><div className="text-sm text-slate-500">Unclaimed coordinator files</div></div>
            </div>
            <div className="text-3xl font-extrabold font-num text-slate-900 dark:text-slate-50">{num(poolCount)}</div>
          </Card>
        )}
      </div>

      <Card className="p-5 mt-6 border-slate-200/80 dark:border-slate-800/80">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-200">Leads by Status</h3>
        </div>
        {l2 ? <Skeleton className="h-72 w-full" /> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byStatus} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <XAxis dataKey="status" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {byStatus.map((e) => <Cell key={e.status} fill={STATUS_COLORS[e.status] || "#3B82F6"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <QuickActions />
    </div>
  );
}
